import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Avatar,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  SportsEsports as GameIcon,
  CalendarToday as CalendarIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';

const TournamentCard = ({ tournament, onClick, variant }) => {
  const navigate = useNavigate();

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'success';
      case 'in_progress':
        return 'warning';
      case 'completed':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'open':
        return 'Abierto';
      case 'in_progress':
        return 'En Progreso';
      case 'completed':
        return 'Finalizado';
      default:
        return status;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'single_elimination':
        return 'Eliminación Simple';
      case 'double_elimination':
        return 'Doble Eliminación';
      case 'round_robin':
        return 'Todos contra Todos';
      default:
        return type;
    }
  };

  const handleClick = (e) => {
    if (onClick) {
      onClick(e, tournament);
    } else {
      navigate(`/tournaments/${tournament.id}`);
    }
  };

  return (
    <Paper
      elevation={0}
      onClick={handleClick}
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          borderColor: 'primary.main'
        },
        ...(variant === 'completed' && {
          opacity: 0.8,
          '&:hover': {
            opacity: 1
          }
        })
      }}
    >
      <Stack spacing={2}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {tournament.name}
          </Typography>
          <Chip
            label={getStatusLabel(tournament.status)}
            color={getStatusColor(tournament.status)}
            size="small"
            sx={{ 
              height: '24px',
              '& .MuiChip-label': { 
                fontSize: '0.75rem',
                px: 1
              }
            }}
          />
        </Box>

        {/* Info Grid */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          gap: 1.5
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GameIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
            <Typography variant="body2" color="text.secondary">
              {getTypeLabel(tournament.type)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
            <Typography variant="body2" color="text.secondary">
              {new Date(tournament.startDate).toLocaleDateString()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
            <Typography variant="body2" color="text.secondary">
              {tournament.participants?.length || 0} participantes
            </Typography>
          </Box>
          {tournament.winner && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrophyIcon sx={{ color: '#c89e38', fontSize: '1.2rem' }} />
              <Typography variant="body2" color="text.secondary">
                Ganador: {tournament.winner.username}
              </Typography>
            </Box>
          )}
        </Box>
      </Stack>
    </Paper>
  );
};

export default TournamentCard; 